import { useState } from 'react';
import { formatCompact } from './TrendChart';
import { OTHER_FILL, arcPath, formatPercent, rampStep } from './viz';

export interface DonutSlice {
  label: string;
  value: number;
  /** Override the ramp step. Use only when the slice has a meaning the ramp cannot carry. */
  color?: string;
}

interface DonutProps {
  slices: DonutSlice[];
  /** Centre caption, e.g. "sessions". The hero number is the total unless `centerValue` is given. */
  centerLabel: string;
  centerValue?: string;
  /** Hard limit is 6. Beyond that a donut is unreadable and the answer is a bar chart. */
  maxSlices?: number;
  size?: number;
  /** Column header for the value in the table twin. */
  valueLabel?: string;
}

const OTHER_LABEL = 'Other';

/**
 * Part-to-whole for a handful of ORDERED categories.
 *
 * <b>Six slices, hard.</b> Past that the arcs are too close in angle to compare and the labels
 * stop fitting; the tail folds into a neutral "Other" that is always shown rather than dropped,
 * because a donut whose slices do not sum to the total is a lie about the shape of the data.
 *
 * <b>The ramp is legal here only because the categories are ordered</b> — exit reasons run
 * best-outcome to worst, duration bins run short to long. Colour follows the slice's fixed
 * position in that order, never its size, so filtering the data never repaints the survivors.
 *
 * <b>Not for a single ratio.</b> A two-slice donut is the classic wrong answer to "what is the
 * bounce rate" — use `ArcMeter`, which encodes one number against its limit and says so.
 *
 * A 2px surface-coloured gap separates adjacent segments (the mark spec's spacer), and the whole
 * thing ships a legend, direct percentages, and a table twin, because a donut cannot be read by
 * a screen reader and the ramp's light end sits under the 3:1 contrast threshold.
 */
export function Donut({
  slices, centerLabel, centerValue, maxSlices = 6, size = 200, valueLabel = 'Sessions',
}: DonutProps) {
  const [hover, setHover] = useState<number | null>(null);

  const usable = slices.filter((s) => Number.isFinite(s.value) && s.value > 0);
  if (usable.length === 0) {
    return <div className="empty-state t-body">No data for this period yet.</div>;
  }

  const cap = Math.min(Math.max(maxSlices, 2), 6);
  const ordered = [...usable];

  // Fold by SIZE but keep the caller's order for colour — the tail that folds is the small tail,
  // while the slices that survive keep the hue their position earned.
  let shown: DonutSlice[] = ordered;
  if (ordered.length > cap) {
    const bySize = [...ordered].sort((a, b) => b.value - a.value);
    const keep = new Set(bySize.slice(0, cap - 1).map((s) => s.label));
    const tail = ordered.filter((s) => !keep.has(s.label));
    shown = [
      ...ordered.filter((s) => keep.has(s.label)),
      { label: OTHER_LABEL, value: tail.reduce((sum, s) => sum + s.value, 0), color: OTHER_FILL },
    ];
  }

  const total = shown.reduce((sum, s) => sum + s.value, 0);
  const colourable = shown.filter((s) => s.label !== OTHER_LABEL).length;

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter * 0.62;

  // 2px of surface between segments, expressed in degrees at the outer radius. Skipped when a
  // slice is the only one, where a gap would open a notch in a solid ring for no reason.
  const gapDeg = shown.length > 1 ? Math.min(3, (2 / (2 * Math.PI * rOuter)) * 360 * 1.6) : 0;

  let cursor = 0;
  const segments = shown.map((slice, i) => {
    const sweep = (slice.value / total) * 360;
    const from = cursor;
    const to = cursor + sweep;
    cursor = to;
    return {
      ...slice,
      share: slice.value / total,
      fill: slice.color ?? rampStep(i, Math.max(colourable, 2)),
      // The gap is taken off the end of each segment, and never eats more than half of a thin one.
      path: arcPath(cx, cy, rInner, rOuter, from, Math.max(from, to - Math.min(gapDeg, sweep / 2))),
      index: i,
    };
  });

  const active = hover !== null ? segments[hover] : null;

  return (
    <div className="donut">
      <div className="donut-figure">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
             aria-label={`${centerLabel}: ${segments.map((s) => `${s.label} ${formatPercent(s.share, 0)}`).join(', ')}`}>
          {segments.map((s) => (
            <path
              key={s.label}
              d={s.path}
              fill={s.fill}
              className="donut-seg"
              opacity={hover === null || hover === s.index ? 1 : 0.35}
              onMouseEnter={() => setHover(s.index)}
              onMouseLeave={() => setHover(null)}
            >
              <title>{`${s.label}: ${formatCompact(s.value)} (${formatPercent(s.share)})`}</title>
            </path>
          ))}

          {/* Hovering swaps the centre to the hovered slice — the tooltip IS the hole, so nothing
              overlaps the ring and no pointer position has to be tracked. */}
          <text x={cx} y={cy - 4} textAnchor="middle" className="donut-hero tabular">
            {active ? formatPercent(active.share, 0) : centerValue ?? formatCompact(total)}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className="donut-sub">
            {active ? truncate(active.label, 14) : centerLabel}
          </text>
        </svg>
      </div>

      <ul className="donut-legend">
        {segments.map((s) => (
          <li
            key={s.label}
            className={`donut-key${hover === s.index ? ' is-active' : ''}`}
            onMouseEnter={() => setHover(s.index)}
            onMouseLeave={() => setHover(null)}
          >
            <i style={{ background: s.fill }} />
            <span className="donut-key-label" title={s.label}>{s.label}</span>
            <span className="donut-key-value t-label tabular">
              {formatCompact(s.value)}
              <span className="subtle"> · {formatPercent(s.share, 0)}</span>
            </span>
          </li>
        ))}
      </ul>

      <details className="viz-table">
        <summary className="t-label">Table view</summary>
        <table className="table">
          <thead>
            <tr><th>Category</th><th className="num">{valueLabel}</th><th className="num">Share</th></tr>
          </thead>
          <tbody>
            {segments.map((s) => (
              <tr key={s.label}>
                <td>{s.label}</td>
                <td className="num">{s.value.toLocaleString('en-US')}</td>
                <td className="num">{formatPercent(s.share)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}
